import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { InfoBanner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardTitle } from "@/components/ui/card";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { EmptyState } from "@/components/ui/feedback";
import { Checkbox } from "@/components/ui/form-controls";
import { LoadingTable } from "@/components/ui/system-state";
import { Table, TableWrapper } from "@/components/ui/table";
import {
  ErrorState,
  NotAvailableState,
  PermissionDeniedState,
} from "@/components/ui/system-state";
import { useApiQuery } from "@/hooks/use-api";
import { formatTemplate, type SsoLocaleCatalog } from "@/i18n/use-sso-locale";
import {
  DENIED_REASON_CODES,
  UNAVAILABLE_REASON_CODES,
  formatTimestamp,
  refusalMessage,
} from "@/features/admin/sso/sso-shared";
import {
  fetchSsoBreakGlass,
  updateSsoBreakGlass,
  withStepUpRetry,
  type ApiErrorInfo,
  type ApiSsoBreakGlassAdmin,
  type ApiSsoBreakGlassSummary,
  type SsoBreakGlassResult,
} from "@/lib/api";

/**
 * Admin → Single sign-on → break-glass administrators (ALL-5464, page-spec
 * action `admin_designates_break_glass_admins`).
 *
 * Calls two operations: `getBreakGlassAdmins` (GET /v1/admin/sso/break-glass)
 * and `updateBreakGlassAdmins` (PUT, a full-set replace). Break-glass is the
 * organization's way back in when the identity provider is unreachable, so the
 * card states which designated administrators are actually live — the contract
 * defines that per admin as "break-glass and signed in within 90 days through a
 * non-ADMIN_API provider", and the summary here is exactly that predicate over
 * the returned rows, not a second rule.
 */

const EMPTY_BREAK_GLASS = { admins: [] as ApiSsoBreakGlassAdmin[], minimum: 0 };

function useSsoBreakGlass(enabled: boolean) {
  const result = useApiQuery(fetchSsoBreakGlass, mapBreakGlassResponse, {
    // A module-level constant, not a fresh object per render: useApiQuery
    // re-runs its effect when `initialData` changes identity, so an inline
    // literal here would refetch on every render.
    initialData: EMPTY_BREAK_GLASS,
    enabled,
  });
  return {
    admins: result.data.admins,
    minimum: result.data.minimum,
    loading: result.loading,
    error: result.error as ApiErrorInfo | null,
    refresh: result.refresh,
  };
}

function mapBreakGlassResponse(raw: SsoBreakGlassResult) {
  return {
    data: { admins: raw.admins, minimum: raw.minimum },
    error: raw.error,
    isEmpty: false,
  };
}

export function AdminSsoBreakGlassCard({
  canManage,
  catalog,
  summary,
  pageStateOwned = false,
}: {
  canManage: boolean;
  catalog: SsoLocaleCatalog;
  /** `SsoOverview.breakGlass` from getSsoOverview. The server owns whether the
   * organization currently has a *usable* way in; the card does not recompute
   * that from the rows it happens to be showing. */
  summary: ApiSsoBreakGlassSummary | null;
  /** True when the domains card already states the page-level denied or
   * unavailable state, so this card must not paint a second copy of it (see
   * sso-admin.tsx). */
  pageStateOwned?: boolean;
}) {
  const ui = catalog.ui;
  const { admins, minimum: listMinimum, loading, error, refresh } = useSsoBreakGlass(canManage);
  const minimum = listMinimum > 0 ? listMinimum : (summary?.minimum ?? 0);
  const [selected, setSelected] = useState<string[]>([]);
  const [notice, setNotice] = useState<{ message: string; supportReference?: string; capability?: string } | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  // The checkbox state mirrors what the API last returned. A failed save never
  // leaves the boxes showing a set the server did not accept.
  useEffect(() => {
    setSelected(admins.filter((admin) => admin.breakGlass).map((admin) => admin.principalId));
  }, [admins]);

  async function handleSave() {
    setNotice(null);
    setSaved(false);
    setBusy(true);
    const outcome = await withStepUpRetry(() => updateSsoBreakGlass(selected));
    setBusy(false);

    if (outcome.stepUpFailed) {
      setNotice({
        message: ui["sso.shared.stepUpDescription"],
        supportReference: outcome.stepUpSupportReference,
      });
      return;
    }
    const result = outcome.result;
    if (!result.ok) {
      setNotice({
        message: refusalMessage(ui, result.reasonCode) ?? ui["sso.breakGlass.failedToLoad"],
        supportReference: result.supportReference,
        capability: result.capability,
      });
      return;
    }
    setSaved(true);
    refresh();
  }

  if (pageStateOwned) return null;

  if (!canManage) {
    return (
      <PermissionDeniedState
        title={ui["sso.shared.deniedTitle"]}
        description={ui["sso.shared.deniedDescription"]}
      />
    );
  }

  if (error && DENIED_REASON_CODES.includes(error.reasonCode)) {
    return (
      <PermissionDeniedState
        title={ui["sso.shared.deniedTitle"]}
        description={ui["sso.shared.deniedDescription"]}
        details={[{ k: "reasonCode", v: error.reasonCode }]}
        footnote={<SupportDetails reference={error.supportReference} capability={error.capability} />}
      />
    );
  }

  if (error && UNAVAILABLE_REASON_CODES.includes(error.reasonCode)) {
    return (
      <div className="mt-5 flex flex-col items-center gap-2">
        <NotAvailableState
          title={ui["sso.shared.unavailableTitle"]}
          description={ui["sso.shared.unavailableDescription"]}
          reason={error.reasonCode}
        />
        <SupportDetails reference={error.supportReference} capability={error.capability} className="justify-center" />
      </div>
    );
  }

  const live = summary?.satisfied ?? false;

  return (
    <Card className="mt-5" data-testid="sso-break-glass-card">
      <CardBody>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>{ui["sso.breakGlass.title"]}</CardTitle>
            <p className="mt-2 max-w-[560px] text-xs leading-relaxed text-muted">
              {ui["sso.breakGlass.description"]}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!loading && !error && (
              <span className="flex items-center gap-2">
                <span className="text-[11px] text-muted">{ui["sso.breakGlass.liveLabel"]}</span>
                <Badge variant={live ? "success" : "warning"} size="sm">
                  {live ? ui["sso.breakGlass.live"] : ui["sso.breakGlass.notLive"]}
                </Badge>
              </span>
            )}
            <Button variant="secondary" size="sm" onClick={refresh} disabled={loading}>
              <RefreshCw aria-hidden className="size-3.5" /> {ui["sso.providers.refresh"]}
            </Button>
            <Button size="sm" loading={busy} onClick={() => void handleSave()} disabled={busy}>
              {busy ? ui["sso.breakGlass.saving"] : ui["sso.breakGlass.save"]}
            </Button>
          </div>
        </div>

        {notice && (
          <div data-testid="sso-break-glass-notice">
            <InfoBanner variant="danger" className="mt-4">
              {notice.message}
              <SupportDetails reference={notice.supportReference} capability={notice.capability} />
            </InfoBanner>
          </div>
        )}
        {saved && (
          <div data-testid="sso-break-glass-saved">
            <InfoBanner variant="success" className="mt-4">{ui["sso.breakGlass.saved"]}</InfoBanner>
          </div>
        )}

        {loading && (
          <div className="mt-4" data-testid="sso-break-glass-loading">
            <span className="sr-only">{ui["sso.breakGlass.loading"]}</span>
            <LoadingTable rows={3} cols={4} />
          </div>
        )}

        {!loading && error && (
          <div className="mt-4 flex flex-col items-center gap-2">
            <ErrorState
              title={ui["sso.breakGlass.failedToLoad"]}
              details={error.reasonCode}
              retryLabel={ui["sso.shared.retry"]}
              onRetry={refresh}
            />
            <SupportDetails reference={error.supportReference} capability={error.capability} className="justify-center" />
          </div>
        )}

        {!loading && !error && admins.length === 0 && (
          <div className="mt-4">
            <EmptyState
              card
              title={ui["sso.breakGlass.emptyTitle"]}
              description={ui["sso.breakGlass.emptyDescription"]}
            />
          </div>
        )}

        {!loading && !error && admins.length > 0 && (
          <div className="mt-4" data-testid="sso-break-glass-table">
            {minimum > 0 && (
              <p className="mb-3 text-xs text-muted">
                {formatTemplate(ui["sso.breakGlass.minimum"], { minimum: String(minimum) })}
              </p>
            )}
            {!live && <p className="mb-3 text-xs text-muted">{ui["sso.breakGlass.notLiveHint"]}</p>}
            <TableWrapper>
              <Table className="dense">
                <thead>
                  <tr className="border-b border-line bg-bg">
                    {[
                      "sso.breakGlass.table.name",
                      "sso.breakGlass.table.email",
                      "sso.breakGlass.table.state",
                      "sso.breakGlass.liveLabel",
                      "sso.breakGlass.table.lastSignIn",
                    ].map((key) => (
                      <th
                        key={key}
                        className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted"
                      >
                        {ui[key]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {admins.map((admin) => (
                    <tr key={admin.principalId} className="border-b border-line last:border-b-0">
                      <td className="px-4 py-[9px] whitespace-nowrap text-[11.5px] text-ink">
                        {admin.displayName}
                      </td>
                      <td className="px-4 py-[9px] whitespace-nowrap font-mono text-[11px] text-ink">
                        {admin.email}
                      </td>
                      <td className="px-4 py-[9px] whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            ariaLabel={admin.email}
                            checked={selected.includes(admin.principalId)}
                            onCheckedChange={(checked) => {
                              setSelected((current) => (
                                checked
                                  ? [...current, admin.principalId]
                                  : current.filter((id) => id !== admin.principalId)
                              ));
                            }}
                            disabled={busy}
                          />
                          <span className="text-[11px] text-muted">
                            {admin.breakGlass ? ui["sso.breakGlass.inSet"] : ui["sso.breakGlass.notInSet"]}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-[9px] whitespace-nowrap">
                        <Badge variant={admin.live ? "success" : "neutral"} size="sm">
                          {admin.live ? ui["sso.breakGlass.liveYes"] : ui["sso.breakGlass.liveNo"]}
                        </Badge>
                      </td>
                      <td className="px-4 py-[9px] whitespace-nowrap text-[11.5px] text-ink">
                        {admin.lastSignInAt ? formatTimestamp(admin.lastSignInAt) : ui["sso.breakGlass.never"]}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
